import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { RENTAL_LISTING_ENTITY } from '../../../constants/entity'
import { MENU_RENTAL_PORTAL_ITEM } from '../../../constants/menu'
import {
  ROUTE_PATH_LIST_RENTAL_PORTAL,
  ROUTE_UI_SECTION_NAME_RENTAL_PORTAL,
  SUBMENU_DEFAULT_VALUE,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { usePageBrowserTabTitle } from '../../../hooks'
import {
  isListViewTypeBoard,
  isListViewTypeWithPreview,
} from '../../../utils/listViewType'
import Button from '../../Button'
import { withDraftProviders } from '../../Draft'
import { FilterBar } from '../../Filter'
import { MultipleViewTypeListLayout } from '../../MultipleViewTypeList'
import { NotificationInline } from '../../Notification'
import { Page, PageActions } from '../../Page'
import { PageActionsButtonAddEntity } from '../../PageActionsButtonAddEntity'
import { getEmptyStateActions, PageEmptyState } from '../../PageEmptyState'
import { PageHeader } from '../../PageHeader'
import {
  getListingsEffectiveViewType,
  getListingsFilteredEmptyTextKey,
  getListingsRefreshErrorText,
  getListingTableItemActions,
  LISTING_CREATE_ACTION_NAME,
  LISTING_DRAFT_PREFIX,
  LISTINGS_INITIAL_RANGE_FILTER_NAMES,
  LISTINGS_SINGLE_FIELD_API,
  QA_NAME_LISTINGS_CLEAR_FILTERS,
  QA_NAME_LISTINGS_PREVIEW,
  QA_NAME_LISTINGS_REFRESH_ERROR,
  QA_NAME_LISTINGS_RETRY,
  QA_NAME_LISTINGS_TABLE,
  QA_NAME_LISTINGS_TABLE_ADD_NEW,
  RentalPortalListingDraftProvider,
} from '../../RentalPortalListing'
import { TableExportData } from '../../Table'
import { TablePagination } from '../../TablePagination'
import Text from '../../Text'
import { useRentalPortalSectionPermissions } from '../RentalPortal.hooks'
import { RentalPortalActiveItemPreview } from '../RentalPortalActiveItemPreview'

import {
  useCreateRentalPortalListing,
  useRentalPortalListingsExport,
  useRentalPortalListingsMassActions,
  useRentalPortalListingsMultipleViewTypeList,
  useRentalPortalListingsTableActions,
  useRentalPortalListingsTablePage,
} from './RentalPortalListings.hooks'
import { getListingsStatusFilterValues } from './RentalPortalListings.utils'
import { RentalPortalListingsTableLayout } from './RentalPortalListingsTableLayout'

import * as styles from './RentalPortalListings.module.css'

import RefreshIcon from '../../../icons/refresh.svg'
import RentalPortalIcon from '../../../icons/rental-portal.svg'

function RentalPortalListings({ ActiveItemPreview }) {
  const { t } = useTranslation()

  const [modal, setModal] = useState(null)

  const tablePage = useRentalPortalListingsTablePage()

  const {
    availableFilters,
    excludeIds,
    filterValues,
    handleApply,
    handleCleanSelection,
    handleClearFilters,
    handleReload,
    handleSetFilterValues,
    handleSetPagination,
    handleSetPreview,
    handleSetView,
    handleShowTableRegime,
    hasActiveFilters,
    isLoading,
    isRefetching,
    listings,
    listViewType: savedListViewType,
    massActionFilterValues,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    preview,
    selectAllPages,
    selected,
    selectedIds,
    serverError,
  } = tablePage

  const hasListings = listings.length > 0

  const listViewType = getListingsEffectiveViewType({
    hasPreview: Boolean(ActiveItemPreview),
    listViewType: savedListViewType,
  })

  const isBoardRegime = isListViewTypeBoard(listViewType)

  const {
    activeItem,
    handleChangeFocusedRow,
    handleChangeViewType,
    handleClickRow,
    handleClosePreview,
    handleMouseDownRow,
    handlePreviewResizeEnd,
    isSplitAvailable,
    layoutListViewType,
  } = useRentalPortalListingsMultipleViewTypeList({
    handleReload,
    handleSetPreview,
    handleSetView,
    handleUpdateConfig: handleApply,
    items: listings,
    listViewType,
    preview,
  })

  const { canCreate } = useRentalPortalSectionPermissions({
    sectionName: ROUTE_UI_SECTION_NAME_RENTAL_PORTAL.listings,
  })

  usePageBrowserTabTitle({
    isLoading,
    pageTitle: t('RentalPortal.ListingsTitleKey'),
  })

  async function handleShowStatusInTable(status) {
    await handleSetFilterValues(
      getListingsStatusFilterValues({ filterValues, status })
    )

    handleShowTableRegime()
  }

  function handleCloseModal() {
    setModal(null)
  }

  const { handleOpenExport } = useRentalPortalListingsExport({
    excludeIds,
    filterValues: massActionFilterValues,
    selectAllPages,
    selectedIds,
    setModal,
    onCleanSelection: handleCleanSelection,
    onClose: handleCloseModal,
  })

  const { handleOpenCreation } = useCreateRentalPortalListing({
    setModal,
    onClose: handleCloseModal,
    onReload: handleReload,
  })

  const { massAction, massActions, setMassAction } =
    useRentalPortalListingsMassActions({
      excludeIds,
      filterValues: massActionFilterValues,
      items: listings,
      pagination,
      selectAllPages,
      selected,
      setModal,
      onCleanSelection: handleCleanSelection,
      onClose: handleCloseModal,
      onReload: handleReload,
    })

  const { setItemAction } = useRentalPortalListingsTableActions({
    setModal,
    onClose: handleCloseModal,
    onPerformAction: massAction,
    onReload: handleReload,
  })

  const hasBodyError = Boolean(serverError) && !hasListings

  const hasStaleError = Boolean(serverError) && hasListings

  const breadcrumbs = [
    {
      id: MENU_RENTAL_PORTAL_ITEM.title,
      title: t(MENU_RENTAL_PORTAL_ITEM.title),
      to: `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}`,
    },
    {
      id: 'RentalPortal.ListingsTitleKey',
      title: t('RentalPortal.ListingsTitleKey'),
    },
  ]

  const shouldShowFilterBar =
    hasListings || hasActiveFilters || isLoading || hasBodyError

  const emptyStateActions = getEmptyStateActions({
    actionName: LISTING_CREATE_ACTION_NAME,
    isLoading,
    qaName: QA_NAME_LISTINGS_TABLE_ADD_NEW,
    shouldShowActions: canCreate && !hasActiveFilters,
    text: t('RentalPortal.ListingAddNewBtnKey'),
    onClick: handleOpenCreation,
  })

  const filteredEmptyTextKey = getListingsFilteredEmptyTextKey({
    filterValues,
    listViewType,
  })

  return (
    <MultipleViewTypeListLayout
      isEmptyItems={!hasListings}
      listViewType={layoutListViewType}
      pageScrollWrapperRef={pageScrollWrapperRef}
      previewProps={{
        Component: RentalPortalActiveItemPreview,
        activeItem,
        canResize: true,
        componentProps: {
          Component: ActiveItemPreview,
          onClose: handleClosePreview,
        },
        emptyProps: {
          EntityIcon: RentalPortalIcon,
          text: t('RentalPortal.ListingPreviewEmptyStateKey'),
          onClosePreview: handleClosePreview,
        },
        qaName: QA_NAME_LISTINGS_PREVIEW,
        resizeConfig: {
          initialWidth: preview?.size ?? undefined,
          onResizeEnd: handlePreviewResizeEnd,
        },
      }}
    >
      <ScreenContainerRef value={pageScrollWrapperRef}>
        <Page
          pagination={
            <TablePagination
              responsive
              disabled={isBoardRegime}
              isSkeleton={isLoading}
              value={pagination}
              onChange={handleSetPagination}
            />
          }
          qaName={QA_NAME_LISTINGS_TABLE}
          scrollWrapperRef={pageScrollWrapperRef}
        >
          <PageHeader
            breadcrumbs={breadcrumbs}
            isLoading={isLoading}
            itemsCount={pagination.count}
            title={t('RentalPortal.ListingsTitleKey')}
          >
            <PageActions className={styles.pageActions}>
              <TableExportData
                containerRef={pageScrollWrapperRef}
                disabled={!selectAllPages && selected.length === 0}
                isSkeleton={isLoading}
                title='RentalPortal.ListingExportBtnKey'
                onClick={handleOpenExport}
              />
              {canCreate ? (
                <PageActionsButtonAddEntity
                  isSkeleton={isLoading}
                  qaName={QA_NAME_LISTINGS_TABLE_ADD_NEW}
                  text={t('RentalPortal.ListingAddNewBtnKey')}
                  onClick={handleOpenCreation}
                />
              ) : null}
            </PageActions>
          </PageHeader>
          {shouldShowFilterBar ? (
            <FilterBar
              hideFilterTemplateSkeleton
              responsive
              apiUrl={LISTINGS_SINGLE_FIELD_API}
              availableFilters={availableFilters}
              canViewFilterTemplate={permissions?.can_view_filter_template}
              entity={RENTAL_LISTING_ENTITY}
              filterValues={filterValues}
              initialRangeFilterNames={LISTINGS_INITIAL_RANGE_FILTER_NAMES}
              isSkeleton={isLoading}
              ordering={ordering}
              pagination={{ per_page: pagination.pageSize }}
              setFilterValues={handleSetFilterValues}
              sub={SUBMENU_DEFAULT_VALUE}
              onReload={handleReload}
            />
          ) : null}
          {hasStaleError ? (
            <NotificationInline
              actions={[
                {
                  beforeIconProps: { Icon: RefreshIcon },
                  callback: handleReload,
                  isLoading: isRefetching,
                  text: t('RentalPortal.ListingRetryBtnKey'),
                },
              ]}
              className={styles.refreshError}
              qaName={QA_NAME_LISTINGS_REFRESH_ERROR}
              text={getListingsRefreshErrorText({
                availableFilters,
                filterValues,
                t,
              })}
              type={NOTIFICATION_TYPE.error}
            />
          ) : null}
          <RentalPortalListingsTableLayout
            activeItem={activeItem}
            arrowNavigationConfig={{
              disabled: !isListViewTypeWithPreview(listViewType),
              focusedRow: activeItem,
            }}
            changeViewTypeButtonProps={{
              hasBoard: true,
              hasSplit: isSplitAvailable && Boolean(ActiveItemPreview),
              listViewType,
              onChange: handleChangeViewType,
            }}
            emptyState={
              <PageEmptyState
                EntityIcon={RentalPortalIcon}
                actions={emptyStateActions}
                text={
                  hasActiveFilters
                    ? undefined
                    : t('RentalPortal.ListingNoDataKey')
                }
              >
                {hasActiveFilters ? (
                  <>
                    <Text isSecondary>{t(filteredEmptyTextKey)}</Text>
                    <Button.Flat
                      qaName={QA_NAME_LISTINGS_CLEAR_FILTERS}
                      text={t('Table.ClearFilterBtnKey')}
                      onClick={handleClearFilters}
                    />
                  </>
                ) : null}
              </PageEmptyState>
            }
            errorState={
              <PageEmptyState EntityIcon={RentalPortalIcon}>
                <Text isSecondary>{t('RentalPortal.ListingLoadErrorKey')}</Text>
                <Button.Flat
                  beforeIconProps={{ Icon: RefreshIcon }}
                  isLoading={isRefetching}
                  qaName={QA_NAME_LISTINGS_RETRY}
                  text={t('RentalPortal.ListingRetryBtnKey')}
                  onClick={handleReload}
                />
              </PageEmptyState>
            }
            getListingItemActions={getListingTableItemActions}
            hasBodyError={hasBodyError}
            hasTable={
              hasListings ||
              isLoading ||
              hasBodyError ||
              isListViewTypeBoard(listViewType) ||
              isListViewTypeWithPreview(listViewType)
            }
            massActions={massActions}
            modal={modal}
            setItemAction={setItemAction}
            setMassAction={setMassAction}
            tablePage={{ ...tablePage, listViewType }}
            onChangeFocusedRow={handleChangeFocusedRow}
            onClickRow={handleClickRow}
            onMouseDownRow={handleMouseDownRow}
            onShowMore={handleShowStatusInTable}
          />
        </Page>
      </ScreenContainerRef>
    </MultipleViewTypeListLayout>
  )
}

export default withDraftProviders(RentalPortalListings, [
  { Provider: RentalPortalListingDraftProvider, prefix: LISTING_DRAFT_PREFIX },
])
